import { useContext, useMemo } from 'react';
import { Input } from 'reactstrap';
import cls from 'clsx';
import { useMutation } from '@tanstack/react-query';
import { useInstanceSelector } from '@s20/hooks/redux.hooks';
import { checkUserHasPermission } from '@s20/selectors/features/permissions.selectors';
import { httpClient } from '@s20/services/httpClient';
import { getStatusName } from '../common';
import { ApiRoutes, ApplicationPermissions } from '../../../../../../Common';
import { JobContext } from '../../JobContext';
import { useUpdateAssignment } from '../../useUpdateAssignment.hook';

export const StaffAssignment = ({
    jobAssignmentId, canEdit, groups, requiredEquipment, assignedEmployee, ...assignment
}) => {
    const dispatchEditPermission = useInstanceSelector(checkUserHasPermission, ApplicationPermissions.dispatch_edit);
    const { staff } = useContext(JobContext);

    const qualifiedStaff = useMemo(() => {
        // does this have required groups?
        if ((groups ?? []).length) {
            // grab objects for all staff that's been assigned for the day
            const todayAssignedStaff = staff
                .filter((a) => !!a.assignment)
                .map((a) => a.assignment);

            // Get any employee groups required by the equipment
            const requiredEmpGroups = groups.map(
                (eg) => eg.id,
            );

            return (todayAssignedStaff ?? []).filter((s) => {
                // Optimistic updates - these staff don't have groups set in the front-end but we know the equipment is assigned to them
                if (!s.groups) {
                    return true;
                }

                // filter the day's staff by the required emp groups.
                const empGroupIds = s.groups.map((grp) => grp.id);
                const hasGroup = empGroupIds.some((groupId) => requiredEmpGroups.includes(groupId));
                return hasGroup;
            });
        }
        return [];
    }, [staff, groups]);

    const mutationHandlers = useUpdateAssignment({
        jobAssignmentId,
        jobId: assignment.jobId,
        onMutate: (equipment, isMatch, resourceId) => {
            if (isMatch) {
                return {
                    ...equipment,
                    assignment: {
                        ...equipment.assignment || {},
                        employeeAssignedId: resourceId,
                    },
                    assignedEmployee: {
                        id: resourceId,
                    },
                };
            }

            return equipment;
        },
    });

    const handleEquipmentAssignmentChange = useMutation({
        mutationFn: (resourceId) => httpClient.js(ApiRoutes.dispatch.setEquipmentAssignedEmployee(jobAssignmentId, resourceId)),
        ...mutationHandlers,
    });

    const employeeAssignedId = assignedEmployee?.id ?? assignment?.employeeAssignedId ?? '';

    return (
        <div className="d-flex flex-column">
            <Input
                type="select"
                placeholder="Assign Staff"
                className={cls({
                    'text-success': !!employeeAssignedId,
                    'text-muted': !employeeAssignedId,
                })}
                style={{ backgroundColor: (!dispatchEditPermission) ? 'transparent' : undefined }}
                value={employeeAssignedId}
                onChange={async (event) => {
                    if (!event.target.value) {
                        event.stopPropagation();
                        return false;
                    }

                    const resourceId = parseInt(event.target.value, 10);

                    await handleEquipmentAssignmentChange.mutateAsync(resourceId);
                }}
                disabled={!dispatchEditPermission || !canEdit}
            >
                <option
                    className="text-muted"
                    value=""
                >
                    Assign Staff
                </option>

                {qualifiedStaff.map((o) => (
                    <option
                        key={o.id}
                        value={o.id}
                    >
                        {o.name}
                    </option>
                ))}
            </Input>

            <span
                className={`assignment-status-label text-right ${getStatusName(assignment)} mr-2`}
            >
                {assignment.confirmationStatusName}
            </span>
        </div>
    );
};